<template>
  <div class="announcement-form-container">
    <!-- 现代化页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-left">
          <el-button @click="$router.go(-1)" size="medium" class="back-btn">
            <i class="el-icon-arrow-left"></i> 返回
          </el-button>
          <div class="header-info">
            <h1 class="page-title">
              <i class="el-icon-bell"></i>
              {{ isEdit ? '编辑公告' : '新增公告' }}
            </h1>
            <p class="page-subtitle">{{ isEdit ? '修改公告内容和设置' : '创建新的系统公告' }}</p>
          </div>
        </div>
        <div class="header-actions">
          <el-button @click="$router.go(-1)" class="action-btn">
            <i class="el-icon-close"></i> 取消
          </el-button>
          <el-button 
            type="primary" 
            @click="handleSubmit" 
            :loading="saving"
            class="action-btn"
          >
            <i class="el-icon-check"></i> {{ isEdit ? '保存修改' : '创建公告' }}
          </el-button>
        </div>
      </div>
    </div>

        <!-- 主要内容区域 -->
    <div class="main-container">
      <!-- 左侧表单区域 -->
      <div class="form-section">
        <el-form 
          :model="announcementForm" 
          :rules="formRules" 
          ref="announcementForm" 
          label-width="120px"
          v-loading="loading"
          class="announcement-form"
        >
          <!-- 基本信息卡片 -->
          <div class="form-card">
            <div class="card-header">
              <h3>
                <i class="el-icon-edit"></i>
                基本信息
              </h3>
              <p>公告的基础信息和内容描述</p>
            </div>
            <div class="card-content">
              <div class="form-grid">
                <div class="form-item">
                  <label class="form-label">
                    <i class="el-icon-chat-line-square"></i>
                    公告标题 <span class="required">*</span>
                  </label>
                  <el-form-item prop="title">
                    <el-input 
                      v-model="announcementForm.title" 
                      placeholder="请输入公告标题" 
                      size="large"
                      maxlength="100"
                      show-word-limit
                    />
                  </el-form-item>
                </div>

                <div class="form-item">
                  <label class="form-label">
                    <i class="el-icon-document"></i>
                    副标题
                  </label>
                  <el-form-item prop="subtitle">
                    <el-input 
                      v-model="announcementForm.subtitle" 
                      placeholder="请输入公告副标题（选填）" 
                      size="large"
                      maxlength="300"
                      show-word-limit
                    />
                  </el-form-item>
                </div>
              </div>

              <div class="form-grid">
                <div class="form-item">
                  <label class="form-label">
                    <i class="el-icon-collection-tag"></i>
                    公告类型 <span class="required">*</span>
                  </label>
                  <el-form-item prop="type">
                    <el-radio-group v-model="announcementForm.type" class="radio-group-type">
                      <el-radio :label="1" border size="medium" class="radio-option">系统公告</el-radio>
                      <el-radio :label="2" border size="medium" class="radio-option">活动公告</el-radio>
                      <el-radio :label="3" border size="medium" class="radio-option">重要通知</el-radio>
                    </el-radio-group>
                  </el-form-item>
                </div>

                <div class="form-item">
                  <label class="form-label">
                    <i class="el-icon-flag"></i>
                    优先级 <span class="required">*</span>
                  </label>
                  <el-form-item prop="priority">
                    <el-radio-group v-model="announcementForm.priority" class="radio-group-priority">
                      <el-radio :label="1" border size="medium" class="radio-option priority-low">
                        <span>低优先级</span>
                      </el-radio>
                      <el-radio :label="2" border size="medium" class="radio-option priority-medium">
                        <span>中优先级</span>
                      </el-radio>
                      <el-radio :label="3" border size="medium" class="radio-option priority-high">
                        <span>高优先级</span>
                      </el-radio>
                    </el-radio-group>
                  </el-form-item>
                </div>
              </div>

              <div class="form-item full-width">
                <label class="form-label">
                  <i class="el-icon-notebook-1"></i>
                  内容摘要
                </label>
                <el-form-item prop="summary">
                  <el-input
                    type="textarea"
                    :rows="3"
                    v-model="announcementForm.summary"
                    placeholder="请输入公告摘要（选填），将在列表页显示"
                    maxlength="500"
                    show-word-limit
                  />
                </el-form-item>
                <div class="form-tip">
                  <i class="el-icon-info"></i>
                  摘要用于在列表页快速了解公告内容，如不填写将自动截取正文内容
                </div>
              </div>

              <div class="form-item full-width">
                <label class="form-label">
                  <i class="el-icon-edit-outline"></i>
                  公告内容 <span class="required">*</span>
                </label>
                <el-form-item prop="content">
                  <rich-text-editor
                    v-model="announcementForm.content"
                    ref="contentEditor"
                    placeholder="请输入公告详细内容，支持插入图片..."
                    height="400px"
                    :image-upload-options="{
                      type: 'other',
                      module: 'announcement'
                    }"
                    @image-upload-success="onImageUploadSuccess"
                    @image-upload-error="onImageUploadError"
                  />
                </el-form-item>
                <div class="form-tip">
                  <i class="el-icon-info"></i>
                  使用富文本编辑器编辑内容，支持文字格式、标题、列表、链接、图片上传等功能
                </div>
              </div>
            </div>
          </div>

          <!-- 发布设置卡片 -->
          <div class="form-card">
            <div class="card-header">
              <h3>
                <i class="el-icon-time"></i>
                发布设置
              </h3>
              <p>设置公告的发布和过期时间</p>
            </div>
            <div class="card-content">
              <div class="form-grid">
                <div class="form-item">
                  <label class="form-label">
                    <i class="el-icon-timer"></i>
                    发布时间
                  </label>
                  <el-switch
                    v-model="enablePublishTime"
                    active-text="定时发布"
                    inactive-text="立即发布"
                    @change="handlePublishTimeChange"
                  />
                  <el-form-item 
                    v-if="enablePublishTime" 
                    prop="publish_time"
                    style="margin-top: 12px;"
                  >
                    <el-date-picker
                      v-model="announcementForm.publish_time"
                      type="datetime"
                      placeholder="选择发布时间"
                      format="yyyy-MM-dd HH:mm:ss"
                      value-format="yyyy-MM-dd HH:mm:ss"
                      size="large"
                      style="width: 100%"
                    />
                  </el-form-item>
                </div>

                <div class="form-item">
                  <label class="form-label">
                    <i class="el-icon-alarm-clock"></i>
                    过期时间
                  </label>
                  <el-switch
                    v-model="enableExpireTime"
                    active-text="设置过期时间"
                    inactive-text="永不过期"
                    @change="handleExpireTimeChange"
                  />
                  <el-form-item 
                    v-if="enableExpireTime" 
                    prop="expire_time"
                    style="margin-top: 12px;"
                  >
                    <el-date-picker
                      v-model="announcementForm.expire_time"
                      type="datetime"
                      placeholder="选择过期时间"
                      format="yyyy-MM-dd HH:mm:ss"
                      value-format="yyyy-MM-dd HH:mm:ss"
                      size="large"
                      style="width: 100%"
                    />
                  </el-form-item>
                </div>
              </div>
            </div>
          </div>
        </el-form>
      </div>

      <!-- 右侧预览区域 -->
      <div class="preview-section">
        <!-- 显示设置卡片 -->
        <div class="preview-card sticky">
          <div class="preview-header">
            <h4>
              <i class="el-icon-setting"></i>
              显示设置
            </h4>
          </div>
          <div class="preview-content">
            <div class="setting-item">
              <label class="setting-label">启用状态</label>
              <el-switch
                v-model="announcementForm.is_active"
                active-text="启用"
                inactive-text="禁用"
                active-color="#67C23A"
                inactive-color="#F56C6C"
              />
              <div class="setting-tip">禁用后公告将不会显示给用户</div>
            </div>

            <div class="setting-item">
              <label class="setting-label">弹窗显示</label>
              <el-switch
                v-model="announcementForm.is_popup"
                active-text="启用弹窗"
                inactive-text="不弹窗"
                active-color="#409EFF"
              />
              <div class="setting-tip">启用后用户登录时会弹窗显示</div>
            </div>

                         <div class="setting-item">
               <label class="setting-label">目标用户</label>
               <el-radio-group v-model="targetUserType" @change="handleTargetUserChange" class="radio-group-target">
                 <el-radio :label="'all'" border class="radio-option-target">
                   全部用户
                 </el-radio>
                 <el-radio :label="'custom'" border class="radio-option-target">
                   指定用户
                 </el-radio>
               </el-radio-group>
              <el-input
                v-if="targetUserType === 'custom'"
                type="textarea"
                :rows="3"
                v-model="targetUserIds"
                placeholder="请输入用户ID，多个用户用逗号分隔"
                style="margin-top: 8px;"
              />
              <div v-if="targetUserType === 'custom'" class="setting-tip">输入用户ID，用英文逗号分隔</div>
            </div>
          </div>
        </div>

        <!-- 预览效果卡片 -->
        <div class="preview-card">
          <div class="preview-header">
            <h4>
              <i class="el-icon-view"></i>
              预览效果
            </h4>
          </div>
          <div class="preview-content">
            <div class="announcement-preview">
              <div class="preview-title">{{ announcementForm.title || '公告标题' }}</div>
              <div class="preview-subtitle" v-if="announcementForm.subtitle">
                {{ announcementForm.subtitle }}
              </div>
              <div class="preview-meta">
                <el-tag v-if="announcementForm.type === 1" size="mini" type="info">系统公告</el-tag>
                <el-tag v-else-if="announcementForm.type === 2" size="mini" type="primary">活动公告</el-tag>
                <el-tag v-else-if="announcementForm.type === 3" size="mini" type="warning">重要通知</el-tag>
                <span class="priority-tag" :class="getPriorityClass(announcementForm.priority)">
                  {{ getPriorityText(announcementForm.priority) }}
                </span>
              </div>
              
              <div class="preview-summary" v-if="announcementForm.summary">
                <div class="summary-label">摘要:</div>
                <div class="summary-content">{{ announcementForm.summary }}</div>
              </div>
              
              <div class="preview-content-section">
                <div class="content-label">正文内容:</div>
                <div 
                  class="rich-content" 
                  v-if="announcementForm.content" 
                  v-html="announcementForm.content"
                ></div>
                <div v-else class="placeholder-content">
                  公告内容将在这里显示...
                </div>
              </div>
              
              <div class="preview-info">
                <div class="info-item">
                  <span class="info-label">状态:</span>
                  <el-tag :type="announcementForm.is_active ? 'success' : 'danger'" size="mini">
                    {{ announcementForm.is_active ? '启用' : '禁用' }}
                  </el-tag>
                </div>
                <div class="info-item">
                  <span class="info-label">弹窗:</span>
                  <el-tag :type="announcementForm.is_popup ? 'primary' : 'info'" size="mini">
                    {{ announcementForm.is_popup ? '是' : '否' }}
                  </el-tag>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getAnnouncement, createAnnouncement, updateAnnouncement } from '@/api/announcement'
import RichTextEditor from '@/components/common/RichTextEditor.vue'

export default {
  name: 'AnnouncementForm',
  
  components: {
    RichTextEditor
  },
  data() {
    return {
      announcementForm: {
        title: '',
        subtitle: '',
        content: '',
        summary: '',
        type: 1,
        priority: 1,
        is_popup: false,
        target_users: null,
        publish_time: '',
        expire_time: '',
        is_active: true
      },
      
      formRules: {
        title: [
          { required: true, message: '请输入公告标题', trigger: 'blur' },
          { min: 2, max: 100, message: '标题长度在 2 到 100 个字符', trigger: 'blur' }
        ],
        subtitle: [
          { max: 300, message: '副标题长度不能超过 300 个字符', trigger: 'blur' }
        ],
        content: [
          { required: true, message: '请输入公告内容', trigger: 'blur' },
          { 
            validator: (rule, value, callback) => {
              if (!value) {
                callback(new Error('请输入公告内容'))
                return
              }
              // 去除HTML标签获取纯文本内容
              const textContent = value.replace(/<[^>]*>/g, '').trim()
              if (textContent.length < 10) {
                callback(new Error('内容长度不能少于 10 个字符'))
              } else {
                callback()
              }
            }, 
            trigger: 'blur' 
          }
        ],
        summary: [
          { max: 500, message: '摘要长度不能超过 500 个字符', trigger: 'blur' }
        ],
        type: [
          { required: true, message: '请选择公告类型', trigger: 'change' }
        ],
        priority: [
          { required: true, message: '请选择优先级', trigger: 'change' }
        ]
      },
      
      loading: false,
      saving: false,
      enablePublishTime: false,
      enableExpireTime: false,
      targetUserType: 'all',
      targetUserIds: ''
    }
  },
  
  computed: {
    isEdit() {
      return !!this.$route.params.id
    },
    
    announcementId() {
      return this.$route.params.id
    }
  },
  
  created() {
    if (this.isEdit) {
      this.fetchAnnouncementData()
    }
  },
  

  
  methods: {
    async fetchAnnouncementData() {
      this.loading = true
      try {
        const response = await getAnnouncement(this.announcementId)
        const announcement = response.data.announcement
        
        // 映射数据到表单
        this.announcementForm = {
          title: announcement.title || '',
          subtitle: announcement.subtitle || '',
          content: announcement.content || '',
          summary: announcement.summary || '',
          type: announcement.type || 1,
          priority: announcement.priority || 1,
          is_popup: announcement.is_popup !== undefined ? announcement.is_popup : false,
          target_users: announcement.target_users || [],
          publish_time: announcement.publish_time || '',
          expire_time: announcement.expire_time || '',
          is_active: announcement.is_active !== undefined ? announcement.is_active : true
        }
        
        // 设置时间开关状态
        this.enablePublishTime = !!announcement.publish_time
        this.enableExpireTime = !!announcement.expire_time
        
        // 设置目标用户
        if (announcement.target_users && announcement.target_users.length > 0) {
          this.targetUserType = 'custom'
          this.targetUserIds = announcement.target_users.join(', ')
        } else {
          this.targetUserType = 'all'
        }
        
      } catch (error) {
        console.error('获取公告信息失败:', error)
        this.$message.error('获取公告信息失败')
        this.$router.go(-1)
      } finally {
        this.loading = false
      }
    },
    
    handleSubmit() {
      this.$refs.announcementForm.validate(async (valid) => {
        if (!valid) {
          this.$message.error('请完善表单信息')
          return
        }
        
        this.saving = true
        try {
          const formData = { ...this.announcementForm }
          
          // 处理发布时间
          if (!this.enablePublishTime) {
            formData.publish_time = ''
          }
          
          // 处理过期时间
          if (!this.enableExpireTime) {
            formData.expire_time = ''
          }
          
          // 处理目标用户
          if (this.targetUserType === 'all') {
            formData.target_users = null
          } else {
            const userIds = this.targetUserIds.split(',')
              .map(id => id.trim())
              .filter(id => id)
              .map(id => parseInt(id))
              .filter(id => !isNaN(id))
            formData.target_users = userIds.length > 0 ? userIds : null
          }
          
          if (this.isEdit) {
            await updateAnnouncement(this.announcementId, formData)
            this.$message.success('公告更新成功')
          } else {
            await createAnnouncement(formData)
            this.$message.success('公告创建成功')
          }
          
          this.$router.push('/admin/announcements')
        } catch (error) {
          this.$message.error(this.isEdit ? '更新失败' : '创建失败')
        } finally {
          this.saving = false
        }
      })
    },
    
    handlePublishTimeChange(value) {
      if (!value) {
        this.announcementForm.publish_time = ''
      }
    },
    
    handleExpireTimeChange(value) {
      if (!value) {
        this.announcementForm.expire_time = ''
      }
    },
    
    handleTargetUserChange(value) {
      if (value === 'all') {
        this.targetUserIds = ''
      }
    },
    
    getPriorityText(priority) {
      const priorityMap = {
        1: '低',
        2: '中',
        3: '高'
      }
      return priorityMap[priority] || '未知'
    },
    
    getPriorityClass(priority) {
      const classMap = {
        1: 'priority-low',
        2: 'priority-medium',
        3: 'priority-high'
      }
      return classMap[priority] || ''
    },
    
    // 富文本编辑器图片上传成功回调
    onImageUploadSuccess({ file, url, response }) {
      // 图片上传成功处理
    },
    
    // 富文本编辑器图片上传失败回调
    onImageUploadError(error) {
      this.$message.error('图片上传失败')
    },
    

  }
}
</script>

<style scoped>
.announcement-form-container {
  min-height: 100vh;
  background: #f5f7fa;
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 复用轮播图表单的基础样式 */
.page-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 25px 40px;
  margin-bottom: 30px;
  box-shadow: 0 2px 10px rgba(102, 126, 234, 0.3);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 20px;
  flex: 1;
}

.back-btn {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  font-weight: 500;
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
}

.header-info {
  flex: 1;
}

.header-info .page-title {
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 12px;
  color: white;
}

.header-info .page-title i {
  font-size: 32px;
  color: white;
}

.header-info .page-subtitle {
  font-size: 16px;
  opacity: 0.9;
  margin: 0;
  line-height: 1.5;
  color: white;
}

.header-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

.action-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.action-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

.main-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 30px;
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: 30px;
  margin-bottom: 40px;
}

.form-section {
  display: flex;
  flex-direction: column;
  gap: 25px;
}

/* 表单卡片样式 */
.form-card {
  background: white;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f3f4;
  overflow: hidden;
  transition: all 0.3s ease;
}

.form-card:hover {
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
}

.card-header {
  padding: 25px 30px 15px;
  border-bottom: 1px solid #f0f2f5;
  background: linear-gradient(135deg, #fafbfc, #f8f9fa);
}

.card-header h3 {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.card-header h3 i {
  color: #667eea;
  font-size: 20px;
}

.card-header p {
  font-size: 14px;
  color: #666;
  margin: 0;
  line-height: 1.5;
}

.card-content {
  padding: 30px;
}

/* 表单网格布局 */
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 25px;
  margin-bottom: 20px;
}

.form-item {
  display: flex;
  flex-direction: column;
}

.form-item.full-width {
  grid-column: 1 / -1;
}

.form-label {
  font-size: 14px;
  font-weight: 600;
  color: #606266;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.form-label i {
  color: #909399;
  font-size: 16px;
}

.form-label .required {
  color: #f56c6c;
  font-weight: 700;
}

/* 单选按钮组优化样式 */
.radio-group-type,
.radio-group-priority {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: stretch;
}

.radio-option {
  width: 100% !important;
  margin: 0 !important;
  height: 44px;
  display: flex !important;
  align-items: center;
  justify-content: flex-start;
  border-radius: 8px !important;
  border: 1px solid #e8eaed !important;
  padding: 0 16px !important;
  transition: all 0.3s ease;
  font-weight: 500;
  box-sizing: border-box;
  min-width: 0;
}

.radio-option:hover {
  border-color: #667eea !important;
  background-color: rgba(102, 126, 234, 0.05) !important;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.15);
}

.radio-option.is-checked {
  border-color: #667eea !important;
  background-color: rgba(102, 126, 234, 0.1) !important;
  color: #667eea !important;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.15);
}

/* 优先级颜色样式优化 - 使用标签形式显示 */
.priority-low span {
  color: #606266;
  position: relative;
}

.priority-low span::before {
  content: '';
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #909399;
  margin-right: 8px;
  vertical-align: middle;
  position: relative;
  top: -1px;
}

.priority-medium span {
  color: #606266;
  position: relative;
}

.priority-medium span::before {
  content: '';
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #e6a23c;
  margin-right: 8px;
  vertical-align: middle;
  position: relative;
  top: -1px;
}

.priority-high span {
  color: #606266;
  position: relative;
}

.priority-high span::before {
  content: '';
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #f56c6c;
  margin-right: 8px;
  vertical-align: middle;
  position: relative;
  top: -1px;
}

/* 选中状态保持文字清晰可读 */
.priority-low.is-checked span,
.priority-medium.is-checked span,
.priority-high.is-checked span {
  color: #667eea !important;
  font-weight: 600;
}

/* 优先级圆点增强效果 */
.priority-low span::before,
.priority-medium span::before, 
.priority-high span::before {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.8);
}

/* 悬停时圆点效果 */
.priority-low:hover span::before {
  box-shadow: 0 2px 4px rgba(144, 147, 153, 0.3);
}

.priority-medium:hover span::before {
  box-shadow: 0 2px 4px rgba(230, 162, 60, 0.3);
}

.priority-high:hover span::before {
  box-shadow: 0 2px 4px rgba(245, 108, 108, 0.3);
}

/* 选中状态下圆点颜色保持不变 */
.priority-low.is-checked span::before {
  background-color: #909399 !important;
}

.priority-medium.is-checked span::before {
  background-color: #e6a23c !important;
}

.priority-high.is-checked span::before {
  background-color: #f56c6c !important;
}

/* 目标用户单选按钮样式 */
.radio-group-target {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: stretch;
}

.radio-option-target {
  width: 100% !important;
  margin: 0 !important;
  height: 40px;
  display: flex !important;
  align-items: center;
  justify-content: flex-start;
  border-radius: 6px !important;
  border: 1px solid #e8eaed !important;
  padding: 0 14px !important;
  transition: all 0.3s ease;
  font-weight: 500;
  font-size: 14px;
  box-sizing: border-box;
  min-width: 0;
}

.radio-option-target:hover {
  border-color: #667eea !important;
  background-color: rgba(102, 126, 234, 0.05) !important;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(102, 126, 234, 0.12);
}

.radio-option-target.is-checked {
  border-color: #667eea !important;
  background-color: rgba(102, 126, 234, 0.1) !important;
  color: #667eea !important;
  box-shadow: 0 2px 6px rgba(102, 126, 234, 0.12);
}

/* 重写Element UI的单选按钮内部样式 */
.radio-option :deep(.el-radio__input) {
  margin-right: 12px;
  position: relative;
  top: 0;
}

.radio-option :deep(.el-radio__label) {
  font-size: 14px;
  font-weight: 500;
  padding-left: 0;
  line-height: 1.4;
}

.radio-option-target :deep(.el-radio__input) {
  margin-right: 10px;
  position: relative;
  top: 0;
}

.radio-option-target :deep(.el-radio__label) {
  font-size: 13px;
  font-weight: 500;
  padding-left: 0;
  line-height: 1.4;
}

/* 确保单选圆点的对齐 */
.radio-option :deep(.el-radio__inner),
.radio-option-target :deep(.el-radio__inner) {
  margin-top: 0;
  vertical-align: middle;
}

/* 统一选中状态的文字颜色（除优先级外） */
.radio-group-type .radio-option.is-checked :deep(.el-radio__label) {
  color: #667eea !important;
}

.radio-group-target .radio-option-target.is-checked :deep(.el-radio__label) {
  color: #667eea !important;
}

/* 禁用状态优化 */
.radio-option:disabled,
.radio-option-target:disabled {
  opacity: 0.6;
  cursor: not-allowed !important;
  background-color: #f5f7fa !important;
}

/* 聚焦状态优化 */
.radio-option:focus-within,
.radio-option-target:focus-within {
  border-color: #667eea !important;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.15) !important;
}

.form-tips {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
  line-height: 1.4;
}

/* 预览区域样式 */
.preview-section {
  position: sticky;
  top: 30px;
  height: fit-content;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.preview-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f3f4;
  overflow: hidden;
}

.preview-card.sticky {
  position: sticky;
  top: 30px;
}

.preview-header {
  padding: 20px 25px 15px;
  border-bottom: 1px solid #f0f2f5;
  background: linear-gradient(135deg, #fafbfc, #f8f9fa);
}

.preview-header h4 {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.preview-header h4 i {
  color: #667eea;
  font-size: 18px;
}

.preview-content {
  padding: 25px;
}

/* 设置项样式 */
.setting-item {
  margin-bottom: 25px;
  padding-bottom: 20px;
  border-bottom: 1px solid #f0f2f5;
}

.setting-item:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.setting-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #606266;
  margin-bottom: 12px;
}

.setting-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 8px;
  line-height: 1.4;
}

.announcement-preview {
  text-align: left;
}

.preview-header {
  margin-bottom: 12px;
}

.preview-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 8px;
  line-height: 1.4;
}

.preview-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.priority-tag {
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 10px;
  color: white;
  font-weight: 500;
}

.priority-low {
  background: #909399;
}

.priority-medium {
  background: #e6a23c;
}

.priority-high {
  background: #f56c6c;
}

.preview-content-section {
  color: #666;
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 16px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #409eff;
  max-height: 200px;
  overflow-y: auto;
}

.preview-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.info-label {
  color: #666;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .main-container {
    grid-template-columns: 1fr 320px;
    gap: 20px;
  }
}

@media (max-width: 992px) {
  .main-container {
    grid-template-columns: 1fr;
    gap: 0;
  }
  
  .form-section {
    margin-bottom: 30px;
  }
  
  .preview-section {
    position: static;
  }
  
  .form-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
}

@media (max-width: 768px) {
  .main-container {
    padding: 0 20px;
  }
  
  .page-header {
    padding: 16px 20px;
  }
  
  .header-content {
    flex-direction: column;
    gap: 15px;
    text-align: center;
  }
  
  .card-content {
    padding: 20px;
  }
  
  .preview-content {
    padding: 20px;
  }
  
  .card-header {
    padding: 20px 20px 15px;
  }
  
  .preview-header {
    padding: 15px 20px 12px;
  }
  
  /* 移动端单选按钮优化 */
  .radio-option {
    height: 40px !important;
    padding: 0 12px !important;
    font-size: 13px;
  }
  
  .radio-option:hover,
  .radio-option.is-checked {
    transform: none;
    box-shadow: none;
  }
  
  .radio-option :deep(.el-radio__label) {
    font-size: 13px !important;
  }
  
  .radio-option-target {
    height: 36px !important;
    padding: 0 12px !important;
    font-size: 13px;
  }
  
  .radio-option-target:hover,
  .radio-option-target.is-checked {
    transform: none;
    box-shadow: none;
  }
  
  .radio-option-target :deep(.el-radio__label) {
    font-size: 12px !important;
  }
  
  /* 移动端优先级圆点调整 */
  .priority-low span::before,
  .priority-medium span::before,
  .priority-high span::before {
    width: 6px;
    height: 6px;
    margin-right: 6px;
  }
  
  .radio-group-type,
  .radio-group-priority {
    gap: 10px;
  }
  
  .radio-group-target {
    gap: 8px;
  }
}

/* 新增字段样式 */
.form-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.preview-subtitle {
  color: #909399;
  font-size: 14px;
  margin-top: 8px;
  line-height: 1.4;
}

.preview-summary {
  margin: 16px 0;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
  border-left: 4px solid #409eff;
}

.summary-label {
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
  font-weight: 600;
}

.summary-content {
  font-size: 14px;
  color: #303133;
  line-height: 1.5;
}

.content-label {
  font-size: 12px;
  color: #666;
  margin-bottom: 8px;
  font-weight: 600;
}

.rich-content {
  font-size: 14px;
  line-height: 1.6;
  color: #303133;
  word-break: break-all;
}

.rich-content h2, .rich-content h3, .rich-content h4 {
  margin: 16px 0 8px 0;
  color: #303133;
}

.rich-content p {
  margin: 8px 0;
  line-height: 1.6;
}

.rich-content ul, .rich-content ol {
  margin: 8px 0;
  padding-left: 24px;
}

.rich-content li {
  margin: 4px 0;
  line-height: 1.6;
}

.rich-content strong {
  font-weight: 600;
  color: #409eff;
}

.rich-content img {
  max-width: 100%;
  height: auto;
  margin: 8px 0;
  border-radius: 4px;
}

/* 预览区域列表和对齐样式 */
.rich-content ul,
.rich-content ol {
  padding-left: 20px;
  margin: 8px 0;
}

.rich-content ul li {
  list-style-type: disc;
  margin: 2px 0;
}

.rich-content ol li {
  list-style-type: decimal;
  margin: 2px 0;
}

.rich-content .ql-align-center {
  text-align: center;
}

.rich-content .ql-align-right {
  text-align: right;
}

.rich-content .ql-align-justify {
  text-align: justify;
}

.rich-content blockquote {
  border-left: 4px solid #ccc;
  margin: 8px 0;
  padding: 4px 12px;
  background-color: #f9f9f9;
  font-style: italic;
}

/* 预览区域字体大小样式 - 支持内联样式和类格式 */
.rich-content span[style*="font-size: 10px"] {
  font-size: 10px !important;
}

.rich-content span[style*="font-size: 12px"] {
  font-size: 12px !important;
}

.rich-content span[style*="font-size: 14px"] {
  font-size: 14px !important;
}

.rich-content span[style*="font-size: 16px"] {
  font-size: 16px !important;
}

.rich-content span[style*="font-size: 18px"] {
  font-size: 18px !important;
}

.rich-content span[style*="font-size: 20px"] {
  font-size: 20px !important;
}

.rich-content span[style*="font-size: 24px"] {
  font-size: 24px !important;
}

.rich-content span[style*="font-size: 32px"] {
  font-size: 32px !important;
}

.rich-content .ql-size-10px {
  font-size: 10px !important;
}

.rich-content .ql-size-12px {
  font-size: 12px !important;
}

.rich-content .ql-size-14px {
  font-size: 14px !important;
}

.rich-content .ql-size-16px {
  font-size: 16px !important;
}

.rich-content .ql-size-18px {
  font-size: 18px !important;
}

.rich-content .ql-size-20px {
  font-size: 20px !important;
}

.rich-content .ql-size-24px {
  font-size: 24px !important;
}

.rich-content .ql-size-32px {
  font-size: 32px !important;
}

.placeholder-content {
  color: #c0c4cc;
  font-style: italic;
}

/* 富文本编辑器样式 - 现代化UI */
::v-deep .quill-editor {
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

::v-deep .ql-toolbar.ql-snow {
  border: 1px solid #e8e8e8;
  border-bottom: none;
  border-radius: 8px 8px 0 0;
  padding: 8px 12px;
  background: #fafafa;
  background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%);
  line-height: 1;
  display: flex;
  align-items: center;
  min-height: 42px;
}

::v-deep .ql-toolbar.ql-snow .ql-formats {
  margin-right: 12px;
  display: flex;
  align-items: center;
  gap: 2px;
}

::v-deep .ql-toolbar.ql-snow .ql-formats:last-child {
  margin-right: 0;
}

::v-deep .ql-container.ql-snow {
  border: 1px solid #e8e8e8;
  border-radius: 0 0 8px 8px;
  font-size: 14px;
  height: 300px;
}

::v-deep .ql-editor {
  padding: 16px 20px;
  line-height: 1.6;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  color: #2c3e50;
  /* 不设置固定字体大小，让字体大小格式生效 */
}

::v-deep .ql-editor.ql-blank::before {
  font-style: normal;
  color: #bbb;
  left: 20px;
  right: 20px;
}

/* 工具栏按钮样式 - 统一高度 */
::v-deep .ql-toolbar button {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #666;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  flex-shrink: 0;
}

::v-deep .ql-toolbar button:hover {
  background: rgba(64, 158, 255, 0.1);
  color: #409eff;
}

::v-deep .ql-toolbar button.ql-active {
  background: #409eff;
  color: #fff;
}

::v-deep .ql-toolbar .ql-picker {
  color: #666;
  transition: all 0.2s ease;
  height: 32px;
  display: inline-flex;
  align-items: center;
}

::v-deep .ql-toolbar .ql-picker:hover {
  color: #409eff;
}

::v-deep .ql-toolbar .ql-picker.ql-expanded {
  color: #409eff;
}

::v-deep .ql-toolbar .ql-picker-label {
  border-radius: 4px;
  padding: 0 8px;
  font-size: 13px;
  line-height: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  min-width: 50px;
}

::v-deep .ql-toolbar .ql-picker-label:hover {
  background: rgba(64, 158, 255, 0.1);
}

::v-deep .ql-toolbar .ql-picker-options {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin-top: 4px;
  z-index: 9999;
  padding: 4px 0;
  min-width: 120px;
}

::v-deep .ql-toolbar .ql-picker-item {
  padding: 8px 12px;
  border-radius: 0;
  margin: 0;
  transition: all 0.2s ease;
  line-height: 1.2;
}

::v-deep .ql-toolbar .ql-picker-item:hover {
  background: rgba(64, 158, 255, 0.1);
  color: #409eff;
}

/* 颜色选择器特殊优化 */
::v-deep .ql-toolbar .ql-color .ql-picker-label,
::v-deep .ql-toolbar .ql-background .ql-picker-label {
  width: 32px;
  min-width: 32px;
  padding: 0;
}

/* 字体大小选择器优化 */
::v-deep .ql-toolbar .ql-size .ql-picker-label {
  min-width: 60px;
  justify-content: center;
}

/* 字体大小选择器样式 */
::v-deep .ql-size .ql-picker-label {
  font-family: inherit !important;
}

::v-deep .ql-size .ql-picker-label[data-value="10px"]::before {
  content: '10px' !important;
}

::v-deep .ql-size .ql-picker-label[data-value="12px"]::before {
  content: '12px' !important;
}

::v-deep .ql-size .ql-picker-label[data-value="14px"]::before {
  content: '14px' !important;
}

::v-deep .ql-size .ql-picker-label[data-value="16px"]::before {
  content: '16px' !important;
}

::v-deep .ql-size .ql-picker-label[data-value="18px"]::before {
  content: '18px' !important;
}

::v-deep .ql-size .ql-picker-label[data-value="20px"]::before {
  content: '20px' !important;
}

::v-deep .ql-size .ql-picker-label[data-value="24px"]::before {
  content: '24px' !important;
}

::v-deep .ql-size .ql-picker-label[data-value="32px"]::before {
  content: '32px' !important;
}

::v-deep .ql-size .ql-picker-label:not([data-value])::before {
  content: '16px' !important;
}

::v-deep .ql-size .ql-picker-item[data-value="10px"]::before {
  content: '10px';
  font-size: 10px;
}

::v-deep .ql-size .ql-picker-item[data-value="12px"]::before {
  content: '12px';
  font-size: 12px;
}

::v-deep .ql-size .ql-picker-item[data-value="14px"]::before {
  content: '14px';
  font-size: 14px;
}

::v-deep .ql-size .ql-picker-item[data-value="16px"]::before {
  content: '16px';
  font-size: 16px;
}

::v-deep .ql-size .ql-picker-item[data-value="18px"]::before {
  content: '18px';
  font-size: 18px;
}

::v-deep .ql-size .ql-picker-item[data-value="20px"]::before {
  content: '20px';
  font-size: 20px;
}

::v-deep .ql-size .ql-picker-item[data-value="24px"]::before {
  content: '24px';
  font-size: 24px;
}

::v-deep .ql-size .ql-picker-item[data-value="32px"]::before {
  content: '32px';
  font-size: 32px;
}

/* 对齐选择器样式 - 强制显示选项 */
::v-deep .ql-align .ql-picker-label {
  min-width: 32px;
  justify-content: center;
}

::v-deep .ql-align .ql-picker-options {
  min-width: 90px !important;
}

::v-deep .ql-align .ql-picker-item {
  padding: 8px 12px !important;
  position: relative;
  line-height: 1.2;
}

::v-deep .ql-align .ql-picker-item::before {
  font-family: inherit !important;
  font-size: 13px !important;
}

::v-deep .ql-align .ql-picker-item:first-child::before {
  content: '左对齐' !important;
}

::v-deep .ql-align .ql-picker-item:nth-child(2)::before {
  content: '居中' !important;
}

::v-deep .ql-align .ql-picker-item:nth-child(3)::before {
  content: '右对齐' !important;
}

::v-deep .ql-align .ql-picker-item:nth-child(4)::before {
  content: '两端对齐' !important;
}

/* 工具提示样式 */
::v-deep .ql-snow .ql-tooltip {
  z-index: 9999;
  background: #2c3e50;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

::v-deep .ql-snow .ql-tooltip input {
  border: 1px solid #e8e8e8;
  border-radius: 3px;
  padding: 4px 8px;
}

/* 富文本内容样式 - 列表和对齐 */
::v-deep .ql-editor {
  padding: 16px 20px;
  line-height: 1.6;
}

/* 字体大小应用样式 - Quill实际使用的格式 */
::v-deep .ql-editor span[style*="font-size: 10px"] {
  font-size: 10px !important;
}

::v-deep .ql-editor span[style*="font-size: 12px"] {
  font-size: 12px !important;
}

::v-deep .ql-editor span[style*="font-size: 14px"] {
  font-size: 14px !important;
}

::v-deep .ql-editor span[style*="font-size: 16px"] {
  font-size: 16px !important;
}

::v-deep .ql-editor span[style*="font-size: 18px"] {
  font-size: 18px !important;
}

::v-deep .ql-editor span[style*="font-size: 20px"] {
  font-size: 20px !important;
}

::v-deep .ql-editor span[style*="font-size: 24px"] {
  font-size: 24px !important;
}

::v-deep .ql-editor span[style*="font-size: 32px"] {
  font-size: 32px !important;
}

/* Quill字体大小类格式支持 */
::v-deep .ql-editor .ql-size-10px {
  font-size: 10px !important;
}

::v-deep .ql-editor .ql-size-12px {
  font-size: 12px !important;
}

::v-deep .ql-editor .ql-size-14px {
  font-size: 14px !important;
}

::v-deep .ql-editor .ql-size-16px {
  font-size: 16px !important;
}

::v-deep .ql-editor .ql-size-18px {
  font-size: 18px !important;
}

::v-deep .ql-editor .ql-size-20px {
  font-size: 20px !important;
}

::v-deep .ql-editor .ql-size-24px {
  font-size: 24px !important;
}

::v-deep .ql-editor .ql-size-32px {
  font-size: 32px !important;
}

/* 确保内联样式生效 - 这是Quill使用的主要方式 */
::v-deep .ql-editor [style] {
  /* 让内联样式优先级更高 */
}

/* 有序列表样式 */
::v-deep .ql-editor ol {
  padding-left: 2em;
  margin: 12px 0;
}

::v-deep .ql-editor ol li {
  list-style-type: decimal;
  margin: 4px 0;
  padding-left: 0.5em;
}

/* 无序列表样式 */
::v-deep .ql-editor ul {
  padding-left: 2em;
  margin: 12px 0;
}

::v-deep .ql-editor ul li {
  list-style-type: disc;
  margin: 4px 0;
  padding-left: 0.5em;
}

/* 对齐样式 */
::v-deep .ql-editor .ql-align-center {
  text-align: center !important;
}

::v-deep .ql-editor .ql-align-right {
  text-align: right !important;
}

::v-deep .ql-editor .ql-align-justify {
  text-align: justify !important;
}

::v-deep .ql-editor p.ql-align-center {
  text-align: center !important;
}

::v-deep .ql-editor p.ql-align-right {
  text-align: right !important;
}

::v-deep .ql-editor p.ql-align-justify {
  text-align: justify !important;
}

/* 引用样式 */
::v-deep .ql-editor blockquote {
  border-left: 4px solid #ccc;
  margin: 16px 0;
  padding: 8px 16px;
  background-color: #f9f9f9;
  font-style: italic;
}

/* 代码块样式 */
::v-deep .ql-editor pre.ql-syntax {
  background-color: #f4f4f4;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin: 12px 0;
  padding: 12px;
  overflow-x: auto;
  font-family: 'Courier New', Courier, monospace;
  font-size: 13px;
}
</style> 